import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { Spin, Descriptions, Card, Tabs, Tag } from "antd";
import axios from "axios";

const { TabPane } = Tabs;

const RealNMITransactionsModal = ({ visible, onClose, order, timezone }) => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("0");
  
  useEffect(() => {
    if (visible && order && order.real_nmi_transactions && order.real_nmi_transactions.length > 0) {
      fetchTransactionDetails();
    }
  }, [visible, order]);
  
  const fetchTransactionDetails = async () => {
    if (!order || !order.real_nmi_transactions || order.real_nmi_transactions.length === 0) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Fetch each transaction's details
      const transactionPromises = order.real_nmi_transactions.map(transId => {
        const token = localStorage.getItem("accessToken");
        return axios.get(`${process.env.REACT_APP_API_BASE_URL}/fetch-data/nmi-transactions/${transId}/`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
      });
      
      const responses = await Promise.all(transactionPromises);
      const transactionData = responses.map(response => response.data);
      setTransactions(transactionData);
    } catch (error) {
      console.error("Error fetching NMI transaction details:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (key) => {
    setActiveTab(key);
  };
  
  // Format currency values
  const formatCurrency = (value) => {
    if (!value) return "$0.00";
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  // Format date values
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Render transaction status with appropriate tag color
  const renderStatus = (condition) => {
    if (!condition) return <Tag>Unknown</Tag>;
    
    switch(condition.toLowerCase()) {
      case 'approved':
        return <Tag color="green">Approved</Tag>;
      case 'complete':
        return <Tag color="green">Complete</Tag>;
      case 'failed':
        return <Tag color="red">Failed</Tag>;
      case 'declined':
        return <Tag color="red">Declined</Tag>;
      case 'pending':
        return <Tag color="orange">Pending</Tag>;
      default:
        return <Tag>{condition}</Tag>;
    }
  };
  
  if (!order) return null;
  
  return (
    <Modal show={visible} onHide={onClose} size="xl" centered dialogClassName="modal-90w">
      <Modal.Header closeButton>
        <Modal.Title>NMI Transactions for Order ID: {order.id}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center p-5">
            <Spin size="large" />
            <p className="mt-3">Loading transaction details...</p>
          </div>
        ) : transactions.length > 0 ? (
          <Tabs activeKey={activeTab} onChange={handleTabChange} type="card">
            {transactions.map((transaction, index) => (
              <TabPane tab={`Transaction #${index + 1}`} key={index.toString()}>
                <Card title={`Transaction ID: ${transaction.transaction_id}`} className="mb-4">
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label="Status">{renderStatus(transaction.condition)}</Descriptions.Item>
                    <Descriptions.Item label="Transaction Type">{transaction.transaction_type}</Descriptions.Item>
                    <Descriptions.Item label="Order ID">{transaction.order_id}</Descriptions.Item>
                    <Descriptions.Item label="Amount">{formatCurrency(transaction.action_amount)}</Descriptions.Item>
                    <Descriptions.Item label="Action Type">{transaction.action_type}</Descriptions.Item>
                    <Descriptions.Item label="Action Date">{formatDate(transaction.action_date)}</Descriptions.Item>
                    <Descriptions.Item label="Gateway Account">{transaction.gateway_account_name}</Descriptions.Item>
                    <Descriptions.Item label="Authorization Code">{transaction.authorization_code || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Response Text">{transaction.action_response_text}</Descriptions.Item>
                    <Descriptions.Item label="Response Code">{transaction.action_response_code}</Descriptions.Item>
                  </Descriptions>
                  
                  <h5 className="mt-4">Customer Information</h5>
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label="Name">{`${transaction.first_name || ''} ${transaction.last_name || ''}`}</Descriptions.Item>
                    <Descriptions.Item label="Email">{transaction.email || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{transaction.phone || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Address">{`${transaction.address_1 || ''} ${transaction.address_2 || ''}`}</Descriptions.Item>
                    <Descriptions.Item label="City">{transaction.city || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="State">{transaction.state || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Postal Code">{transaction.postal_code || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Country">{transaction.country || "N/A"}</Descriptions.Item>
                  </Descriptions>
                  
                  <h5 className="mt-4">Payment Information</h5>
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label="Card Number">{transaction.cc_number || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Card Type">{transaction.cc_type || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Expiration">{transaction.cc_exp || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="AVS Response">{transaction.avs_response || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="CSC Response">{transaction.csc_response || "N/A"}</Descriptions.Item>
                    <Descriptions.Item label="Entry Mode">{transaction.entry_mode || "N/A"}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </TabPane>
            ))}
          </Tabs>
        ) : (
          <p className="text-muted">No NMI transaction details available.</p>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RealNMITransactionsModal;
